import {useCallback, useMemo, useState} from 'react';
import {OnChangeEventType} from '@vlsergey/react-bootstrap-pagination/lib/cjs/PaginationWrapper';

export type KeyDataPair = [string | number, Record<string, string | number>];

export type SortStateItem = {
  key: string;
  direction: 'asc' | 'desc';
};

export const PAGE_SIZE_ALL = Infinity;

export interface PathxTableProps {
  sortCol: string;
  sortAsc: boolean;
  onSortingChange: (sort: string, direction: boolean) => void;
  page: number;
  setPage: (page: number) => void;
  onPageChange: ({target}: OnChangeEventType) => void;
  reset: () => void;
  selected: any;
  onSelectedChange: (id, data) => void;
  onSelectedChangeBatch: (keyDataPair: KeyDataPair[]) => void;
  setSelected: (obj: any) => void;
  filterData: any;
  filter: any;
  setFilter: (f: any) => void;
  ordering: string | null;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
}

type Props = {
  defaultPageSize?: number;
  defaultSortCol?: string;
  defaultSortDir?: 'asc' | 'desc';
  defaultFilter?: Record<string, any>;
};

export const usePathxTable = ({
  defaultPageSize = 25,
  defaultSortCol = '',
  defaultSortDir = 'asc',
  defaultFilter,
}: Props = {}) => {
  const [sortState, setSortState] = useState<SortStateItem[]>(
    defaultSortCol ? [{key: defaultSortCol, direction: defaultSortDir}] : [],
  );
  const ordering = useMemo(() => {
    if (!sortState.length) return null;

    return sortState.map(item => (item.direction === 'desc' ? '-' : '') + item.key).join(',');
  }, [sortState]);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState({});
  const [filter, privateSetFilter] = useState<Record<string, any>>(defaultFilter ?? {});
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const setFilter = useCallback(
    (f: any) => {
      if (typeof f !== 'function') privateSetFilter(f);
      else
        privateSetFilter((...args) => {
          const res = f(...args);
          // eslint-disable-next-line no-restricted-syntax
          for (const key in res) if (Array.isArray(res[key]) && !res[key].length) delete res[key];
          return res;
        });
    },
    [privateSetFilter],
  );

  const resetSortState = useCallback(() => {
    setPage(0);
    setSortState(defaultSortCol ? [{key: defaultSortCol, direction: defaultSortDir}] : []);
    setSelected({});
  }, [defaultSortCol, defaultSortDir]);

  const resetFilterState = useCallback(() => {
    setPage(0);
    setFilter({});
    setSelected({});
  }, [setFilter]);

  const resetFilterToDefault = useCallback(() => {
    setPage(0);
    setFilter(defaultFilter ?? {});
    setSelected({});
  }, [setFilter, defaultFilter]);

  const reset = useCallback(() => {
    resetSortState();
    resetFilterState();
  }, [resetSortState, resetFilterState]);

  const onPageChange = useCallback(({target}: OnChangeEventType) => {
    setPage(target.value);
  }, []);

  const onSortingChange = (sort: string, direction: boolean | 'default') => {
    setSortState(prev => {
      const newState = [...prev];
      // eslint-disable-next-line no-nested-ternary
      const newDirection = direction ? 'asc' : 'desc';
      const existingIndex = prev.findIndex(item => item.key === sort);
      const item = {key: sort, direction: newDirection} as const;
      if (direction === 'default')
        if (existingIndex >= 0) newState.splice(existingIndex, 1);
        else newState.push(item);
      else if (existingIndex >= 0) newState[existingIndex] = item;
      else newState.push(item);
      return newState;
    });
    setPage(0);
  };
  const onSelectedChangeBatch = (keyDataPair: KeyDataPair[]) => {
    const newSelected = {...selected};
    keyDataPair.forEach(([id, data]) => {
      if (newSelected[id]) {
        delete newSelected[id];
      } else {
        newSelected[id] = data;
      }
    });
    setSelected(newSelected);
  };
  const onSelectedChange = (id, data) => onSelectedChangeBatch([[id, data]]);

  const onPageSizeChange = (val: number) => {
    setPageSize(val);
    if (val === PAGE_SIZE_ALL) {
      setPage(0);
    }
  };

  const onFilterChange = useCallback(
    (name: string, value: any) => {
      setPage(0);
      setFilter(prev => ({...prev, [name]: value}));
    },
    [setFilter, setPage],
  );

  const onFilterRefresh = useCallback(() => {
    setFilter(prev => ({...prev}));
    setPage(0);
  }, [setFilter, setPage]);
  const onFilterReset = useCallback(() => {
    setFilter({});
    setPage(0);
  }, [setFilter, setPage]);

  const filterData = useMemo(() => {
    const data = {};
    Object.keys(filter)
      .filter(filterKey => !!filter[filterKey])
      .forEach(filterKey => {
        const value = filter[filterKey];
        if (Array.isArray(value)) {
          data[filterKey] = JSON.stringify(value.map(val => val.value));
        } else if (typeof value === 'object') {
          data[filterKey] = value.value;
        } else {
          data[filterKey] = value;
        }
      });
    return data;
  }, [filter]);
  return {
    sortState,
    onSortingChange,
    page,
    setPage,
    onPageChange,
    reset,
    resetSortState,
    resetFilterState,
    selected,
    onSelectedChange,
    onSelectedChangeBatch,
    onFilterChange,
    onFilterRefresh,
    onFilterReset,
    resetFilterToDefault,
    filterData,
    filter,
    setFilter,
    ordering,
    setSelected,
    pageSize,
    onPageSizeChange,
    setSortState,
  };
};
